import { hasProductPackages } from '@/lib/package-utils'
import React, { useMemo, useState } from 'react'
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, CardContent } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Checkbox } from '@/components/ui/checkbox'
import MyCheckbox from '@/components/zthd/MyCheckbox'
import { formatUSD, formatPercent } from '@/lib/format-utils'
import { isDateDataComplete, getWeek } from '@/lib/date-utils'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import { DropdownPid2, DropdownPid3, usePid2 } from '@/components/DropdownPid'
import { DropdownPackageName } from '@/components/DropdownPackage'
import { DropdownCountryCombined } from '@/components/DropdownCountry'
import { MyDatePicker } from '@/components/MyDatePicker'
import ContrastModeSelector from '@/components/roi/ContrastModeSelector'

export const description = 'A line chart'

const chartRoiConfig = {
  profit: {
    label: 'Profit',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  revenue: {
    label: 'Total Revenue',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  cost: {
    label: 'Total Cost',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  cpi: {
    label: 'CPI',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  cpn: {
    label: 'C/N',
    color: 'hsl(var(--chart-8))',
    formatter: (value) => formatUSD(value, 4, 4),
    yAxis: 'right',
  },
  er: {
    label: 'ER',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => formatPercent(value, 1, 1),
    yAxis: 'right',
  },
  'day0.roas': {
    label: 'Day0 ROAS',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => formatPercent(value, 1, 1),
    yAxis: 'right',
  },
  'day3.roas': {
    label: 'Day3 ROAS',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => formatPercent(value, 1, 1),
    yAxis: 'right',
  },
}

const userMetricsConfig = {
  dau: {
    label: 'DAU',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => (value / 10000).toFixed(2) + 'W',
    yAxis: 'right',
  },
  dnu: {
    label: 'DNU',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => (value / 10000).toFixed(2) + 'W',
    yAxis: 'right',
  },
  installs: {
    label: 'Installs',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
}

const revenueDetailsConfig = {
  'revenues.admob': {
    label: 'Admob',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.admob_waterfall': {
    label: 'Admob Waterfall',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.facebook': {
    label: 'Facebook',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.pangle': {
    label: 'Pangle',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.applovin': {
    label: 'AppLovin',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.applovin_admob': {
    label: 'AppLovin Admob',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.unity': {
    label: 'Unity',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.vungle': {
    label: 'Vungle',
    color: 'hsl(var(--chart-8))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.mintegral': {
    label: 'Mintegral',
    color: 'hsl(var(--chart-9))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.apple': {
    label: 'Apple Sub',
    color: 'hsl(var(--chart-10))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.ironsource': {
    label: 'Ironsource',
    color: 'hsl(var(--chart-11))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.fyber': {
    label: 'Fyber',
    color: 'hsl(var(--chart-12))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.moloco': {
    label: 'Moloco',
    color: 'hsl(var(--chart-13))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.google_play_subscription': {
    label: 'GP Sub',
    color: 'hsl(var(--chart-14))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'revenues.other': {
    label: 'Other',
    color: 'hsl(var(--chart-15))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
}

const costDetailsConfig = {
  'costs.facebook': {
    label: 'Facebook',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.facebook_vo': {
    label: 'Facebook VO',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.ads': {
    label: 'Ads',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.ads_30': {
    label: 'Ads 30',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.apple_ads': {
    label: 'Apple Ads',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.tiktok': {
    label: 'Tiktok',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
  'costs.unity_ads': {
    label: 'Unity Ads',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'left',
  },
}

const costCPIConfig = {
  'costs.facebook.cpi': {
    label: 'facebook',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.facebook_vo.cpi': {
    label: 'facebook vo',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.ads.cpi': {
    label: 'ads',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.ads_30.cpi': {
    label: 'ads 30',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.apple_ads.cpi': {
    label: 'apple ads',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.tiktok.cpi': {
    label: 'tiktok',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
  'costs.unity_ads.cpi': {
    label: 'unity ads',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => formatUSD(value, 3, 3),
    yAxis: 'right',
  },
}

const costInstallConfig = {
  'costs.facebook.installs': {
    label: 'Facebook',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.facebook_vo.installs': {
    label: 'Facebook VO',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.ads.installs': {
    label: 'Ads',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.ads_30.installs': {
    label: 'Ads 30',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.apple_ads.installs': {
    label: 'Apple Ads',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.tiktok.installs': {
    label: 'Tiktok',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
  'costs.unity_ads.installs': {
    label: 'Unity Ads',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => value.toLocaleString(),
    yAxis: 'right',
  },
}

const chartConfig = {
  ...chartRoiConfig,
  ...userMetricsConfig,
  ...revenueDetailsConfig,
  ...costDetailsConfig,
  ...costCPIConfig,
  ...costInstallConfig,
}

// 生成对比数据的配置
const getContrastConfig = (baseConfig) => {
  const contrastConfig = {}
  Object.keys(baseConfig).forEach((key) => {
    const base = baseConfig[key]
    contrastConfig[`${key}.contrast`] = {
      label: `${base.label} (对比)`,
      color: base.color, // 使用与原始配置相同的颜色
      formatter: base.formatter,
      yAxis: base.yAxis,
    }
  })
  return contrastConfig
}

const contrastChartConfig = {
  ...getContrastConfig(chartRoiConfig),
  ...getContrastConfig(userMetricsConfig),
  ...getContrastConfig(revenueDetailsConfig),
  ...getContrastConfig(costDetailsConfig),
  ...getContrastConfig(costCPIConfig),
  ...getContrastConfig(costInstallConfig),
}

// 合并所有配置
const allChartConfig = {
  ...chartConfig,
  ...contrastChartConfig,
}

const metricGroups = {
  all: {
    label: 'All',
    metrics: Object.keys(chartRoiConfig),
  },
  users: {
    label: 'User Metrics',
    metrics: Object.keys(userMetricsConfig),
  },
  revenueDetails: {
    label: 'Revenue Details',
    metrics: Object.keys(revenueDetailsConfig),
  },
  costDetails: {
    label: 'Cost Details',
    metrics: Object.keys(costDetailsConfig),
  },
  costCpiDetails: {
    label: 'Cost CPI',
    metrics: Object.keys(costCPIConfig),
  },
  installsDetails: {
    label: 'Installs',
    metrics: Object.keys(costInstallConfig),
  },
}

const RoiChart = ({ data }) => {
  const [selectedMetrics, setSelectedMetrics] = useState([
    'profit',
    'revenue',
    'cost',
  ])

  const [showLabel, setShowLabel] = useState(false)
  const [showIncompleteData, setShowIncompleteData] = useState(false)
  // 控制哪些组是展开的，默认只展开 all 和 users
  const [expandedGroups, setExpandedGroups] = useState({
    all: true,
    users: true,
    revenueDetails: false,
    costDetails: false,
    costCpiDetails: false,
    installsDetails: false,
  })
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const pids = usePid2((state) => state.pids)
  const pid = pids.join(',') === '' ? 'total' : pids.join(',')
  // 按产品声明显示子包筛选。
  const showPackageFilter = hasProductPackages(pid)

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const processedData = useMemo(() => {
    return data
      .map((item) => {
        // 检查当前日期数据是否完整
        const isCurrentDataComplete = showIncompleteData || isDateDataComplete(item.date, false)
        
        // 在对比模式下，检查对比数据是否存在
        let hasContrastData = false
        if (contrastEnabled && item.contrast) {
          hasContrastData = item.contrast.revenue != null || 
                           item.contrast.cost != null || 
                           item.contrast.profit != null ||
                           item.contrast.dnu != null ||
                           item.contrast.dau != null ||
                           item.contrast.installs != null ||
                           item.contrast.er != null ||
                           item.contrast.day0Roas != null ||
                           item.contrast.day3Roas != null ||
                           (item.contrast.revenues && Object.keys(item.contrast.revenues).length > 0) ||
                           (item.contrast.costs && Object.keys(item.contrast.costs).length > 0)
        }
        
        // 判断是否应该显示当前数据
        // 如果"不完整数据"开关关闭，且当前数据不完整，则不显示当前数据（即使有数据值）
        const shouldShowCurrentData = isCurrentDataComplete
        
        // 正常处理数据
        const processed = {
          ...item,
          date: String(item.date).slice(4, 8),
          originalDate: item.date, // 保存原始日期用于tooltip显示周几
          contrastDate: item.contrast?.date, // 保存对比日期用于tooltip显示
        }
        
        // 如果"不完整数据"开关关闭，且当前数据不完整，将当前数据值设为undefined
        // 即使数据源存在且有值，也要设为undefined
        if (!shouldShowCurrentData) {
          processed.revenue = undefined
          processed.cost = undefined
          processed.profit = undefined
          processed.installs = undefined
          processed.dnu = undefined
          processed.dau = undefined
          processed.er = undefined
          processed['day0.roas'] = undefined
          processed['day3.roas'] = undefined
          processed.cpi = undefined
          processed.cpn = undefined
          // 清空所有revenues和costs相关数据（但不包括对比数据）
          Object.keys(processed).forEach(key => {
            if (key.startsWith('revenues.') || key.startsWith('costs.')) {
              if (!key.endsWith('.contrast')) {
                processed[key] = undefined
              }
            }
          })
        } else {
          // 检查数据源是否存在，如果数据源不存在，将值设为undefined而不是0
          // 这样可以区分"数据不存在"和"数据值为0"
          // 在DataTable中，如果revenues不存在，revenue会被设为0，但item.revenues会是undefined
          if (item.revenues === undefined && item.revenue === 0) {
            processed.revenue = undefined
          }
          if (item.costs === undefined && item.cost === 0) {
            processed.cost = undefined
          }
          // profit 是计算值，如果revenues和costs都不存在，且profit为0，则设为undefined
          if (item.revenues === undefined && item.costs === undefined && item.profit === 0) {
            processed.profit = undefined
          }
          if (item.costs === undefined && item.installs === 0) {
            processed.installs = undefined
          }
          // dnu和dau来自dnus数据源，如果不存在且值为0，设为undefined
          if (item.dnu === undefined || item.dnu === null) {
            processed.dnu = undefined
          }
          if (item.dau === undefined || item.dau === null) {
            processed.dau = undefined
          }
        }
        
        // 只有在应该显示当前数据时才处理这些值
        if (shouldShowCurrentData) {
          // day0 ROAS to percentage for right Y axis (align with ER scale)
          const roasRatio = item?.day0Roas?.avg_roas
          if (typeof roasRatio === 'number') {
            processed['day0.roas'] = roasRatio
          }
          const day3RoasRatio = item?.day3Roas?.avg_roas
          if (typeof day3RoasRatio === 'number') {
            processed['day3.roas'] = day3RoasRatio
          }
          const er = item?.er
          if (typeof er === 'number') {
            processed['er'] = er / 100
          }
          
          // 如果数据源不存在，将计算值设为undefined
          if (item.costs?.total?.installs) {
            processed['cpi'] = item.costs.total.costs / item.costs.total.installs
          } else {
            processed['cpi'] = undefined
          }
          if (item.dnu) {
            processed['cpn'] = item.costs?.total?.costs / item.dnu
          } else {
            processed['cpn'] = undefined
          }
          
          Object.entries(item.revenues || {}).forEach(([key, value]) => {
            processed[`revenues.${key}`] = value
          })
          Object.entries(item.costs || {}).forEach(([key, value]) => {
            processed[`costs.${key}`] = value.costs
            if (value.installs) {
              processed[`costs.${key}.cpi`] = value.costs / value.installs
            } else {
              processed[`costs.${key}.cpi`] = undefined
            }
          })
        }

        // 处理对比数据（如果对比数据存在，总是处理，不受当前数据是否完整影响）
        if (contrastEnabled && item.contrast && hasContrastData) {
          const contrast = item.contrast
          // 主要指标对比
          if (contrast.revenue != null) processed['revenue.contrast'] = contrast.revenue
          if (contrast.cost != null) processed['cost.contrast'] = contrast.cost
          if (contrast.profit != null) processed['profit.contrast'] = contrast.profit
          if (contrast.dnu != null) processed['dnu.contrast'] = contrast.dnu
          if (contrast.dau != null) processed['dau.contrast'] = contrast.dau
          if (contrast.installs != null) processed['installs.contrast'] = contrast.installs
          
          // ER对比
          if (contrast.er != null) {
            processed['er.contrast'] = contrast.er / 100
          }
          
          // ROAS对比
          if (contrast.day0Roas?.avg_roas != null) {
            processed['day0.roas.contrast'] = contrast.day0Roas.avg_roas
          }
          if (contrast.day3Roas?.avg_roas != null) {
            processed['day3.roas.contrast'] = contrast.day3Roas.avg_roas
          }
          
          // CPI和CPN对比
          if (contrast.costs?.total?.installs && contrast.costs?.total?.costs != null) {
            processed['cpi.contrast'] = contrast.costs.total.costs / contrast.costs.total.installs
          }
          if (contrast.dnu && contrast.costs?.total?.costs != null) {
            processed['cpn.contrast'] = contrast.costs.total.costs / contrast.dnu
          }
          
          // 详细revenue对比
          Object.entries(contrast.revenues || {}).forEach(([key, value]) => {
            if (key !== 'total') {
              processed[`revenues.${key}.contrast`] = value
            }
          })
          
          // 详细cost对比
          Object.entries(contrast.costs || {}).forEach(([key, value]) => {
            if (key !== 'total' && value?.costs != null) {
              processed[`costs.${key}.contrast`] = value.costs
              if (value.installs) {
                processed[`costs.${key}.cpi.contrast`] = value.costs / value.installs
              }
              if (value.installs != null) {
                processed[`costs.${key}.installs.contrast`] = value.installs
              }
            }
          })
        } else if (contrastEnabled && !hasContrastData) {
          // 如果对比模式开启但对比数据不存在，将对比数据也设为undefined
          Object.keys(processed).forEach(key => {
            if (key.endsWith('.contrast')) {
              processed[key] = undefined
            }
          })
        }

        return processed
      })
      .reverse()
  }, [data, showIncompleteData, contrastEnabled])

  // 分离主要组和折叠组
  const mainGroups = ['all', 'users']
  const collapsibleGroups = ['revenueDetails', 'costDetails', 'costCpiDetails', 'installsDetails']

  return (
    <Card className="shadow-none border-none">
      {/* 筛选组件，与表格顶部同步 */}
      <div className="flex items-center gap-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1 px-6 pt-4 pb-2 border-b">
        <DropdownPid2 />
        <DropdownPid3 />
        {showPackageFilter && <DropdownPackageName />}
        <DropdownCountryCombined />
        <MyDatePicker getMaxDate="complete" showMaxDate={true} />
        <ContrastModeSelector />
      </div>
      <div className="px-6 pt-4 space-y-4">
        {/* 主要组：All 和 User Metrics，始终显示 */}
        <div className="flex flex-wrap gap-4">
          {mainGroups.map((groupKey) => {
            const group = metricGroups[groupKey]
            return (
              <div key={groupKey} className="space-y-2">
                <h3 className="text-sm font-semibold">{group.label}</h3>
                <div className="flex flex-wrap gap-4">
                  {group.metrics.map((metricKey) => {
                    const metric = chartConfig[metricKey]
                    return (
                      <div key={metricKey} className="flex items-center space-x-2">
                        <Checkbox
                          id={metricKey}
                          checked={selectedMetrics.includes(metricKey)}
                          onCheckedChange={(checked) => {
                            setSelectedMetrics(
                              checked
                                ? [...selectedMetrics, metricKey]
                                : selectedMetrics.filter((m) => m !== metricKey),
                            )
                          }}
                        />
                        <label
                          htmlFor={metricKey}
                          className="text-sm font-medium"
                          style={{ color: metric.color }}
                        >
                          {metric.label}
                        </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* 折叠组：其他组，可以折叠，放在单独的一行 */}
        <div className="flex flex-wrap gap-4">
          {collapsibleGroups.map((groupKey) => {
            const group = metricGroups[groupKey]
            const isExpanded = expandedGroups[groupKey]
            return (
              <div key={groupKey} className={isExpanded ? 'w-full space-y-2' : 'space-y-2'}>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  className="text-sm font-semibold hover:text-primary flex items-center gap-1"
                >
                  <span>{isExpanded ? '▼' : '▶'}</span>
                  <span>{group.label}</span>
                </button>
                {isExpanded && (
                  <div className="flex flex-wrap gap-4 w-full">
                    {group.metrics.map((metricKey) => {
                      const metric = chartConfig[metricKey]
                      return (
                        <div key={metricKey} className="flex items-center space-x-2">
                          <Checkbox
                            id={metricKey}
                            checked={selectedMetrics.includes(metricKey)}
                            onCheckedChange={(checked) => {
                              setSelectedMetrics(
                                checked
                                  ? [...selectedMetrics, metricKey]
                                  : selectedMetrics.filter((m) => m !== metricKey),
                              )
                            }}
                          />
                          <label
                            htmlFor={metricKey}
                            className="text-sm font-medium"
                            style={{ color: metric.color }}
                          >
                            {metric.label}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex items-center gap-4 px-6 mt-4">
        <MyCheckbox
          label="Show Label"
          checked={showLabel}
          onCheckedChange={setShowLabel}
        />
        <div className="flex items-center gap-2">
          <MyCheckbox
            label="不完整数据"
            checked={showIncompleteData}
            onCheckedChange={setShowIncompleteData}
          />
          <span className="text-sm text-muted-foreground">
            (下午4点更新前一天完整数据)
          </span>
        </div>
      </div>
      <CardContent>
        <ChartContainer
          className="aspect-auto h-[500px] w-full"
          config={allChartConfig}
        >
          <LineChart
            data={processedData}
            margin={{ top: 20, right: 50, bottom: 20, left: 50 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value, payload) => {
                const originalDate = payload?.payload?.originalDate
                const contrastDate = payload?.payload?.contrastDate

                if (originalDate) {
                  const year = String(originalDate).slice(0, 4)
                  const month = String(originalDate).slice(4, 6)
                  const day = String(originalDate).slice(6, 8)
                  const [, weekDay] = getWeek(originalDate)
                  const currentLabel = `${year}/${month}/${day} 周${weekDay}`

                  // 如果有对比日期，返回数组实现换行
                  if (contrastEnabled && contrastDate) {
                    const cYear = String(contrastDate).slice(0, 4)
                    const cMonth = String(contrastDate).slice(4, 6)
                    const cDay = String(contrastDate).slice(6, 8)
                    const [, cWeekDay] = getWeek(contrastDate)
                    const contrastLabel = `${cYear}/${cMonth}/${cDay} 周${cWeekDay}`
                    return [currentLabel, contrastLabel]
                  }

                  return currentLabel
                }
                return value
              }}
            />
            <YAxis
              yAxisId="left"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => formatUSD(value, 0, 0)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <ChartTooltip
              cursor={false}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const originalDate = payload[0]?.payload?.originalDate
                  const contrastDate = payload[0]?.payload?.contrastDate

                  // 格式化当前日期
                  let currentDateLabel = null
                  if (originalDate) {
                    const year = String(originalDate).slice(0, 4)
                    const month = String(originalDate).slice(4, 6)
                    const day = String(originalDate).slice(6, 8)
                    const [, weekDay] = getWeek(originalDate)
                    currentDateLabel = `${year}/${month}/${day} 周${weekDay}`
                  }

                  // 格式化对比日期
                  let contrastDateLabel = null
                  if (contrastEnabled && contrastDate) {
                    const cYear = String(contrastDate).slice(0, 4)
                    const cMonth = String(contrastDate).slice(4, 6)
                    const cDay = String(contrastDate).slice(6, 8)
                    const [, cWeekDay] = getWeek(contrastDate)
                    contrastDateLabel = `${cYear}/${cMonth}/${cDay} 周${cWeekDay}`
                  }

                  return (
                    <div className="bg-background border p-2 rounded-md shadow-md min-w-[200px]">
                      <div className="mb-2">
                        {currentDateLabel && (
                          <p className="font-semibold text-foreground">
                            {currentDateLabel}
                          </p>
                        )}
                        {contrastDateLabel && (
                          <p className="text-xs text-muted-foreground">
                            {contrastDateLabel}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        {payload.map((pld) => {
                          const dataKey = pld.dataKey
                          const config = allChartConfig[dataKey]
                          const isContrast = dataKey.endsWith('.contrast')
                          const color = pld.color
                          const value = pld.value

                          if (value === undefined || value === null) return null

                          return (
                            <div
                              key={dataKey}
                              className={`flex justify-between items-center ${
                                isContrast ? 'text-xs text-muted-foreground' : 'text-foreground'
                              }`}
                            >
                              <div className="flex items-center">
                                <div
                                  className="w-2 h-2 rounded-full mr-2"
                                  style={{
                                    backgroundColor: color,
                                    opacity: isContrast ? 0.6 : 1,
                                  }}
                                />
                                <span>{config?.label || dataKey}</span>
                              </div>
                              <span>
                                {config?.formatter ? config.formatter(value) : value}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            {selectedMetrics.map((metricKey) => {
              const metric = chartConfig[metricKey]
              const contrastKey = `${metricKey}.contrast`
              const contrastMetric = contrastChartConfig[contrastKey]
              
              return (
                <React.Fragment key={metricKey}>
                  {/* 当前数据线 */}
                  <Line
                    type="monotone"
                    dataKey={metricKey}
                    stroke={metric.color}
                    yAxisId={metric.yAxis}
                    formatter={metric.formatter}
                    strokeWidth={2}
                    dot={{ fill: metric.color }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  >
                    {showLabel && (
                      <LabelList
                        position="top"
                        offset={12}
                        formatter={metric.formatter}
                      />
                    )}
                  </Line>
                  {/* 对比数据线 */}
                  {contrastEnabled && contrastMetric && (
                    <Line
                      type="monotone"
                      dataKey={contrastKey}
                      stroke={contrastMetric.color}
                      yAxisId={contrastMetric.yAxis}
                      formatter={contrastMetric.formatter}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      strokeOpacity={0.6}
                      dot={{ fill: contrastMetric.color, r: 3, fillOpacity: 0.6 }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    >
                      {showLabel && (
                        <LabelList
                          position="top"
                          offset={12}
                          formatter={contrastMetric.formatter}
                        />
                      )}
                    </Line>
                  )}
                </React.Fragment>
              )
            })}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default RoiChart
